<template>
    <div class="write-stage" title="">
        <section class="content">
            <div class="content-wrapper">
                <div
                    v-if="isSmsSendingBlockedByVerificationStatus"
                    class="verification-status"
                    data-qa="ktv-sms-verification-status"
                >
                    <KtvSmsVerificationStatusAlert
                        render-if-sending-blocked
                        show-button
                        :message="smsAccountVerification.errorDescription"
                        :rejection-date="smsAccountVerification.rejectionDate"
                        :verification-status="smsAccountVerification.status"
                    />
                </div>

                <div class="subtitle">
                    Name your broadcast
                </div>

                <div class="broadcast-name">
                    <DsInputField
                        autocomplete="no"
                        class="sms-input broadcast-name-field"
                        :label="$options.MESSAGE_CONSTANTS.broadcastTitle"
                        :value="broadcast.title"
                        :invalid="showNameError"
                        required
                        :submitted="showNameError"
                        @input="updateTitle"
                    />

                    <DsPopover
                        :is-open="showFromNameTooltip"
                        position="right"
                        position-fixed
                        @close="showFromNameTooltip = false"
                    >
                        <template #reference>
                            <DsIconButton
                                class="tooltip-button"
                                name="help-circle"
                                @click="showFromNameTooltip = !showFromNameTooltip"
                                @mouseover="showFromNameTooltip = true"
                                @mouseleave="showFromNameTooltip = false"
                            />
                        </template>

                        <template #default>
                            <div class="from-tooltip">
                                The title of your text message broadcast is only visible to you. TIP: Give it a descriptive name so it’s easy to find in your list of sent broadcasts.
                            </div>
                        </template>
                    </DsPopover>

                    <span v-if="showNameError" class="broadcast-name-error">{{ $options.MESSAGE_CONSTANTS.invalidName }}</span>
                </div>

                <div class="from-container">
                    <DsInputField
                        class="sms-input"
                        :label="$options.MESSAGE_CONSTANTS.from"
                        readonly
                        :value="formattedFromNumber"
                    />

                    <DsPopover
                        :is-open="showFromTooltip"
                        position="right"
                        position-fixed
                        @close="showFromTooltip = false"
                    >
                        <template #reference>
                            <DsIconButton
                                class="tooltip-button"
                                name="help-circle"
                                @click="showFromTooltip = !showFromTooltip"
                                @mouseover="showFromTooltip = true"
                                @mouseleave="showFromTooltip = false"
                            />
                        </template>

                        <template #default>
                            <div class="from-tooltip">
                                <div>This is your <span class="text-bold">Keap Marketing Number</span>. Recipients will see it each time you send a text message broadcast or automated text message.</div>
                            </div>
                        </template>
                    </DsPopover>
                </div>

                <section class="audience">
                    <div class="subtitle">
                        Pick your audience
                    </div>

                    <SmsBroadcastRecipientSearch
                        :contact-count="contactCount"
                        :invalid="showRecipientError"
                        :opt-statuses="optStatuses"
                        :recipient-criteria="broadcast.recipientCriteria"
                        :recipients="recipients"
                        @blur="handleRecipientBlur"
                        @focus="recipientSearchHasFocus = true"
                        @update="updateRecipients"
                    />

                    <SmsConsentAlert />

                    <SmsBroadcastMaxRecipientsAlert v-if="totalValidRecipientCount > $options.MAX_RECIPIENTS" class="max-recipients-alert" />
                </section>

                <section class="message">
                    <div class="subtitle">
                        Write your message
                    </div>

                    <div class="text-box-container">
                        <SmsTextBox
                            v-model="broadcast.messageContent"
                            :has-error="showMessageError"
                            :sms-broadcast-enabled="smsBroadcastEnabled"
                            :app-id="appId"
                            :recipient-count="totalValidRecipientCount"
                            :user-id="userId ? userId.toString() : '0'"
                            source="broadcast"
                            @input="updateMessage"
                        />
                    </div>

                    <SmsInvalidCharacterAlert v-if="!messageContainsValidCharacters" />
                </section>

                <section class="phone-options">
                    <div class="subtitle">
                        Review phone number delivery options
                    </div>

                    <p class="description">
                        Which phone number(s) do you want to send to? (You can select multiple)
                    </p>

                    <div class="phone-options-container">
                        <SmsPhoneOptionsSelector
                            v-model="phoneOptions"
                            class="phone-type-selector"
                            :show-error="showPhoneOptionsError"
                            :opt-statuses="optStatuses"
                            :recipients="recipients"
                            @input="updatePhoneTypes"
                        >
                            <template #footer>
                                <SmsBroadcastRecipientCount
                                    class="phone-number-count"
                                    :invalid="showError"
                                    :opt-statuses="optStatuses"
                                    :recipients="recipients"
                                />
                            </template>
                        </SmsPhoneOptionsSelector>
                    </div>

                    <div class="details-button">
                        <DsLink as="button" :trailing-icon="areDetailsOpen ? 'chevron-up' : 'chevron-down'" @click="areDetailsOpen = !areDetailsOpen">
                            How do phone number types work?
                        </DsLink>
                    </div>

                    <div v-if="areDetailsOpen" class="details">
                        Contact records can contain phone numbers from Phone 1 to Phone 5. Each number can be assigned a phone type (Mobile, Work, etc.). Make sure you review the phone numbers in your contact records so you don’t have issues sending messages.
                    </div>
                </section>

                <div class="button-row">
                    <DsFilledButton
                        data-qa="sms-broadcast-write-stage-next-button"
                        :disabled="isSmsSendingBlockedByVerificationStatus"
                        @click="$emit('next')"
                    >
                        {{ $options.MESSAGE_CONSTANTS.next }}
                    </DsFilledButton>
                </div>

                <ErrorBanner v-if="showErrorBanner" :message="errorMessage" />

                <div class="sms-broadcast-feedback">
                    <p class="feedback-cta">
                        {{ $options.MESSAGE_CONSTANTS.letUsNow }}
                    </p>

                    <DsTextButton class="small" data-qa="feedback-button" @click="isFeedbackModalOpen = true">
                        {{ $options.MESSAGE_CONSTANTS.giveFeedback }}
                    </DsTextButton>
                </div>
                <div class="sms-broadcast-footer-wrapper">
                    <SmsBroadcastFooter />
                </div>
            </div>
        </section>

        <preview
            class="preview"
            :account="smsAccount"
            :message="broadcast.messageContent"
            @show-settings="$emit('show-settings')"
        />

        <portal to="root">
            <SmsFeedbackModal
                v-if="isFeedbackModalOpen"
                source="Max Classic Broadcast"
                @close="isFeedbackModalOpen = false"
            />
        </portal>
        <DsFocusOnKeyboardOnly />
    </div>
</template>

<script>
import ErrorBanner from '@/components/shared/ErrorBanner';
import SmsBroadcastFooter from '@/components/broadcast/sms/SmsBroadcastFooter';
import SmsBroadcastMaxRecipientsAlert from '@/components/broadcast/sms/SmsBroadcastMaxRecipientsAlert';
import SmsBroadcastRecipientSearch from '@/components/broadcast/sms/SmsBroadcastRecipientSearch';
import SmsFeedbackModal from '@/components/shared/SmsFeedbackModal';
import SmsPhoneOptionsSelector from '@/components/automatedSms/SmsPhoneOptionsSelector';
import SmsTextBox from '@/components/automatedSms/SmsTextBox';
import { containsSupportedCharacters } from '@/components/automatedSms/automatedSms.util';
import SmsInvalidCharacterAlert from '@/components/automatedSms/SmsInvalidCharacterAlert';
import Preview from '@/components/automatedSms/SmsPreview';
import SmsConsentAlert from '@/components/broadcast/sms/SmsConsentAlert';
import KtvSmsVerificationStatusAlert from '@/components/automatedSms/KtvSmsVerificationStatusAlert';

import {
    SUPPORTED_PHONE_FIELDS,
    SUPPORTED_PHONE_TYPES,
} from '@/components/automatedSms/sms.constants';
import {
    KMN_VERIFICATION_STATUS_TYPE,
    MAX_RECIPIENTS,
    SMS_MESSAGE_LENGTH,
    STAGE_STATUS,
} from '@/components/broadcast/constants/smsBroadcast.constants';
import { BLUR_DELAY } from '@/components/shared/constants/timing.constants';
import { getValidRecipientCount } from '@/components/broadcast/utils/smsBroadcast.util';
import { formatPhone } from '@/utils/phone.util';
import SmsBroadcastRecipientCount from '@/components/broadcast/sms/SmsBroadcastRecipientCount';

export const MESSAGE_CONSTANTS = {
    subTitle: 'Write your text message broadcast',
    from: 'From',
    broadcastTitle: 'Broadcast name',
    enterMessage: 'Enter message',
    complianceMessage: 'Compliance Tip: You cannot send messages about high-risk financial services, debt forgiveness, illegal substances, get rich quick schemes, or phishing',
    next: 'Review broadcast',
    letUsNow: 'Missing something? Let us know',
    giveFeedback: 'Give feedback',
    invalidName: 'Please enter a name',
    invalidContact: 'Add at least one contact',
    titleError: 'Please enter a broadcast name to continue',
    messageRequired: 'Please enter a message to continue',
    multipleErrors: 'There are multiple errors in this text message. Please correct them to continue.',
    noContactsError: 'None of your recipients have a phone number that matches the phone number types you selected.  Please add more phone number types, and be sure to review your recipients’ contact record phone data.',
    recipientError: 'None of the recipients you’ve added to this broadcast have a phone number. Be sure to visit the contact record for each recipient to add phone numbers.',
    noRecipientsError: 'Please enter at least 1 recipient to continue.',
    preview: 'Preview',
    phoneOptionsError: 'Your phone number delivery options are empty. Please select at least 1 phone number type to continue.',
};
export default {
    MAX_RECIPIENTS,
    MESSAGE_CONSTANTS,

    components: {
        SmsBroadcastRecipientSearch,
        ErrorBanner,
        SmsBroadcastFooter,
        SmsBroadcastMaxRecipientsAlert,
        SmsFeedbackModal,
        SmsPhoneOptionsSelector,
        SmsBroadcastRecipientCount,
        SmsTextBox,
        SmsInvalidCharacterAlert,
        Preview,
        SmsConsentAlert,
        KtvSmsVerificationStatusAlert,
    },

    props: {
        contactCount: {
            type: Number,
            default: 0,
        },
        status: {
            type: String,
            required: true,
        },
        optStatuses: {
            type: Array,
            default: () => [],
        },
        broadcast: {
            type: Object,
            required: true,
        },
        recipients: {
            type: Array,
            default: () => [],
        },
        showError: Boolean,
        from: {
            type: String,
            default: '',
        },
        contactsWithMultiplePhones: {
            type: Array,
            default: () => [],
        },
        userId: {
            type: Number,
            required: true,
        },
        appId: {
            type: String,
            required: true,
        },
        smsBroadcastEnabled: Boolean,
        smsAccount: {
            type: Object,
            default: () => ({}),
        },
        smsAccountVerification: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            isOpen: false,
            loading: false,
            marketingAutomatedSmsId: null,
            messageContent: '',
            showFromTooltip: false,
            showFromNameTooltip: false,
            isFeedbackModalOpen: false,
            fromNumberPhone: '',
            broadcastTitle: '',
            contactNumber: '',
            isContactNumberInvalid: false,
            isBroadcastTitleValid: false,
            recipientSearchHasFocus: false,
            areDetailsOpen: false,
        };
    },

    computed: {
        phoneOptions: {
            set() {},
            get() {
                return [...this.broadcast.phoneTypes, ...this.broadcast.phoneFields];
            },
        },

        isPhoneTypesValid() {
            return this.broadcast.phoneTypes.length > 0;
        },

        errorMessage() {
            const errors = [];

            if (this.broadcast.messageContent?.length > this.maxCharacterCount) {
                errors.push(this.$tc('sms.broadcast.writeStage.error.messageTooLong', this.maxCharacterCount, { count: this.maxCharacterCount }));
            }

            if (!this.broadcast.title) {
                errors.push(MESSAGE_CONSTANTS.titleError);
            }

            if (!this.messageContainsValidCharacters) {
                errors.push(this.$t('sms.broadcast.richTextBox.error.unsupportedCharacters'));
            }

            if (!this.broadcast.messageContent) {
                errors.push(MESSAGE_CONSTANTS.messageRequired);
            }

            if (this.isPhoneOptionsInvalid) {
                errors.push(MESSAGE_CONSTANTS.phoneOptionsError);
            }

            if (this.contactCount === 0) {
                errors.push(MESSAGE_CONSTANTS.noRecipientsError);
            } else if (!this.isPhoneOptionsInvalid && this.totalValidRecipientCount === 0) {
                if (!this.allPhoneOptionsSelected) {
                    errors.push(MESSAGE_CONSTANTS.noContactsError);
                } else {
                    errors.push(MESSAGE_CONSTANTS.recipientError);
                }
            }

            if (errors.length > 1) {
                return MESSAGE_CONSTANTS.multipleErrors;
            }

            if (errors.length === 1) {
                return errors[0];
            }

            return '';
        },

        formattedFromNumber() {
            return formatPhone(this.from);
        },

        isMessageValid() {
            return this.broadcast.messageContent?.length > 0 && this.broadcast.messageContent?.length <= this.maxCharacterCount && this.messageContainsValidCharacters;
        },

        showNameError() {
            return !this.broadcast.title && (this.isStatusInvalid || this.isBroadcastTitleValid);
        },

        showContactError() {
            return !this.contactNumber && this.isContactNumberInvalid;
        },
        showMessageError() {
            return !this.broadcast.messageContent && (this.isStatusInvalid || this.showError) || !this.messageContainsValidCharacters;
        },
        showRecipientError() {
            return ((this.noValidRecipients && this.allPhoneOptionsSelected) || this.contactCount === 0) && this.isStatusInvalid;
        },
        noValidRecipients() {
            return this.totalValidRecipientCount === 0;
        },
        isStatusInvalid() {
            return this.status === STAGE_STATUS.INVALID;
        },
        showErrorBanner() {
            return (this.isStatusInvalid || this.showError) && this.errorMessage;
        },
        totalValidRecipientCount() {
            const { optStatuses, recipients } = this;

            return getValidRecipientCount({ optStatuses, recipients });
        },

        allPhoneOptionsSelected() {
            return SUPPORTED_PHONE_TYPES.every((supportedPhoneType) => this.broadcast.phoneTypes.includes(supportedPhoneType))
                    && SUPPORTED_PHONE_FIELDS.every((supportedPhoneField) => this.broadcast.phoneFields.includes(supportedPhoneField));
        },

        isPhoneOptionsInvalid() {
            return Array.isArray(this.phoneOptions) && this.phoneOptions.length === 0;
        },

        showPhoneOptionsError() {
            return (this.isPhoneOptionsInvalid || this.noValidRecipients) && this.isStatusInvalid && !this.allPhoneOptionsSelected;
        },

        messageContainsValidCharacters() {
            return containsSupportedCharacters(this.broadcast.messageContent);
        },

        maxCharacterCount() {
            return SMS_MESSAGE_LENGTH.MAXIMUM;
        },

        isSmsSendingBlockedByVerificationStatus() {
            return this.smsAccountVerification.status
                && this.smsAccountVerification.status !== KMN_VERIFICATION_STATUS_TYPE.APPROVED;
        },
    },

    methods: {
        updatePhoneTypes(phoneOptions) {
            const upperPhoneTypes = SUPPORTED_PHONE_TYPES.map((phoneType) => phoneType.toUpperCase());
            const upperPhoneFields = SUPPORTED_PHONE_FIELDS.map((phoneField) => phoneField.toUpperCase());

            const phoneTypes = phoneOptions.filter((option) => upperPhoneTypes.includes(option.toUpperCase()));
            const phoneFields = phoneOptions.filter((option) => upperPhoneFields.includes(option.toUpperCase()));

            this.$emit('update', {
                broadcast: { phoneTypes, phoneFields },
            });

            if (this.isPhoneTypesValid && this.showError) {
                this.showError = false;
            }
        },

        handleDone() {
            if (this.isMessageValid) {
                window.parent.dispatchEvent(new Event('automationMessageReady'));
            } else {
                this.showError = true;
            }

            if (!this.broadcast.title) {
                this.isBroadcastTitleValid = true;
            }

            if (!this.contactNumber) {
                this.isContactNumberInvalid = true;
            }
        },

        updateMessage(messageContent) {
            if (this.messageContainsValidCharacters) {
                this.$emit('update', {
                    broadcast: { messageContent },
                });
            }
        },

        updateRecipients({ recipientCriteria }) {
            this.$emit('update', {
                broadcast: { recipientCriteria },
            });
        },

        updateTitle(title) {
            this.$emit('update', {
                broadcast: { title },
            });
        },

        updateContact(contact) {
            this.contactNumber = contact;
        },

        handleRecipientBlur() {
            setTimeout(() => {
                this.recipientSearchHasFocus = false;
            }, BLUR_DELAY);
        },
    },
};
</script>


<style lang="scss">
// Without this, the bootstrap.css global css is overriding the default popover css
div.write-stage .templates-popover .popover .title {
    padding: $spacing-025 0;
    background-color: unset;
    border: none;
}

div.write-stage .templates-popover .popover.open {
    padding: 0;
}

</style>

<style lang="scss" scoped>

.button-row {
    justify-content: flex-start;
    margin-bottom: $spacing-200;
    margin-top: $spacing-400;
}

.content {
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 3;
    overflow-x: hidden;

    &-wrapper {
        padding-top: $spacing-400;
        height: 100%;
        width: 70%;
        display: flex;
        flex-direction: column;

        @media($small) {
            width: 100%;
            padding: $spacing-200 $spacing-300 0;
        }
    }
}

.error-banner {
    margin-bottom: $spacing-200;
}

.title {
    font-family: $font-family-secondary-title;
    font-weight: 600;
    padding-bottom: $spacing-300;
}

.from-container {
    display: flex;
}

.tooltip-button {
    margin: $spacing-050 $spacing-100 0;

    --icon-color: #{$color-gray-darker};
    --icon-size: 1rem;
}

.from-tooltip {
    padding: $spacing-200;
    font-size: $font-size-md;
    line-height: $line-height-lg;
    .text-bold {
        font-weight: $font-weight-bold;
    }
}

.sms-input {
    max-width: px-to-rem(328px);
    width: 100%;

    @media($small) {
        max-width: initial;
    }
}
.small {
    padding: 8px;
    font-weight: 500;

}
.input-field.sms-input.broadcast-name-field label {
    font-size: 15px;
}

.broadcast-name {
    display: flex;
}

.broadcast-name, .to-phone {
    padding-bottom: 1rem;
}

.broadcast-name-field, .phone-field {
    margin-bottom: 5px;
}

.broadcast-name-error {
    font-size: small;
    color: #e02500;
}

.sms-broadcast-footer-wrapper {
    margin-top: auto;
}

.sms-broadcast-feedback {
    display: flex;
    align-items: center;
    font-size: $font-size-xs;
    gap: $spacing-200;

    .feedback-cta {
        margin-bottom: 0;
        color: $color-gray-700;
    }
}
.preview {
    flex: 2;
}

div.write-stage {
    display: inline-grid;
    grid-template-columns: 62% 38%;
    height: calc(100vh - 3.5rem);
}

.audience, .phone-options {
    margin-top: 0.66rem;
}

.subtitle {
    font-size: $font-size-md;
    line-height: $line-height-lg;
    font-weight: $font-weight-semibold;
    margin-bottom: $spacing-100;
}

.description {
    color: $color-ink-900;
    font-size: px-to-rem(14px);
    margin-bottom: $spacing-100;
}

.details {
    font-size: px-to-rem(14px);
    color: $color-ink-900;
    line-height: $line-height-md;
    margin-top: $spacing-100 ;
}

.phone-options .subtitle {
    margin-bottom: 0;
}

.phone-options-container {
    display: flex;
    flex-direction: column;
}

.phone-number-count {
    display: flex;
    flex-direction: row-reverse;
    margin-bottom: $spacing-200;
}

.phone-number-count {
    display: flex;
    flex-direction: row-reverse;
}

.text-box-container {
    display: flex;
    flex-direction: column;
}
</style>

<style lang="scss">
.write-stage .from-container {
    .popover {
        width: 31.25rem;
    }
}
.preview {
    flex: 2;
}

.verification-status:not(:empty) {
    margin-bottom: $spacing-300;
    width: 100%;
}
</style>
